import { Box, IconButton, Typography, Tooltip, Chip } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePermissions } from '../../../../context/PermissionContext';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import { PAGE_IDS, PERMISSION_IDS } from '../../../../constants/permissions';
import { useNotification } from '../../../../hooks/useNotification';
import { ApplicantProfileData } from '../../../../pages/rec/applicant/ApplicantProfile';
import api from '../../../../services/httpClient';
import { APPLICANT_ENDPOINTS } from '../../../../services/endpoints/rec/applicant.endpoints';
import { SETTINGS_ENDPOINTS } from '../../../../services/endpoints/settings.endpoints';
import { SYSTEM_ENDPOINTS } from '../../../../services/endpoints/system.endpoints';
import { formatDate } from '../../../../utils/date';
import { isValidEmail } from '../../../../utils/common';
import { AsyncAutocomplete } from '../../../common/AsyncAutocomplete';
import Button from '../../../common/Button';
import DatePickerField from '../../../common/DatePickerField';
import EditIcon from '../../../common/icons/EditIcon';
import Loading from '../../../common/Loading';
import Notification from '../../../common/Notification';
import SelectField from '../../../common/SelectField';
import StatusChip, { StatusConfig } from '../../../common/StatusChip';
import CustomTable from '../../../common/table/CustomTable';
import { Column } from '../../../common/table/types';
import TextField from '../../../common/TextField';

interface HistoryApiResponse {
  history: Array<{
    applicantId: number;
    firstName: string | null;
    lastName: string | null;
    email: string;
    issueId: string;
    currentStatus: string;
    historyId: number;
    changelogId: string;
    statusFrom: string;
    statusTo: string;
    timeInStatus: number;
    createdDate: string;
    createdBy: string;
  }>;
}

interface HistoryProps {
  setSummary: (summary: ApplicantProfileData['basicInfo']) => void;
}

interface HistoryType {
  statusFrom: string;
  statusTo: string;
  createdDate: string;
  createdBy: string;
  timeInStatus: string;
}

interface InterviewPanelMember {
  id: number;
  interviewerName: string;
  interviewerEmail: string;
  createdAt: string;
}

interface Interview {
  id: number;
  title: string;
  date: string;
  startTime: string;
  followUpDate: string;
  followUpStatus: number;
  followUpConfirmedAt: string | null;
  interviewType: string;
  candidateId: number;
  rescheduleCount: number;
  cancellationOption: number | null;
  isDelete: number;
  createdAt: string;
  panel: InterviewPanelMember[];
}

interface InterviewApiResponse {
  interviews: Interview[];
}

interface InterviewType {
  interviewType: string;
  createdDate: string;
  interviewDate: string;
  confirmationDate: string;
  confirmedDate: string;
  status: string;
  interviewPanel: InterviewPanelMember[];
}

const statusConfig: StatusConfig = {
  "OpenCV": { label: 'Open CV', color: '#5656FF', backgroundColor: '#5656FF1A' },
  "Short Listed": { label: 'Shortlisted', color: '#E7B317', backgroundColor: '#FBB32C1A' },
  "Interview Called": { label: 'Interview Called', color: '#702DFF', backgroundColor: '#702DFF1A' },
  "Interviewed": { label: 'Interviewed', color: '#3E217D', backgroundColor: '#3E217D1A' },
  "Test Called": { label: 'Test Called', color: '#03BCC2', backgroundColor: '#03BCC21A' },
  "Tested": { label: 'Tested', color: '#00839E', backgroundColor: '#00839E1A' },
  "Technical Interview Called": { label: 'Technical Interview Called', color: '#BE0BAA', backgroundColor: '#BE0BAA1A' },
  "Technical Interviewed": { label: 'Technical Interviewed', color: '#870578', backgroundColor: '#8705781A' },
  "Client Interview Called": { label: 'Client Interview Called', color: '#030099', backgroundColor: '#0300991A' },
  "Client Interviewed": { label: 'Client Interviewed', color: '#040268', backgroundColor: '#0402681A' },
  "Final Interview Called": { label: 'Final Interview Called', color: '#BDBD00', backgroundColor: '#BDBD001A' },
  "Final Interviewed": { label: 'Final Interviewed', color: '#999900', backgroundColor: '#9999001A' },
  "Selected": { label: 'Selected', color: '#937AFF', backgroundColor: '#937AFF1A' },
  "Offered": { label: 'Offered', color: '#528900', backgroundColor: '#5289001A' },
  "Joined": { label: 'Joined', color: '#528900', backgroundColor: '#5289001A' },
  "Rejected": { label: 'Rejected', color: '#E9004A', backgroundColor: '#E9004A1A' },
  "No Vacancy": { label: 'No Vacancy', color: '#9C9C9C', backgroundColor: '#9C9C9C1A' }
};

const interviewStatusConfig: StatusConfig = {
  "Scheduled": { label: 'Scheduled', color: '#69af00ff', backgroundColor: '#5289001A' },
  "Rescheduled": { label: 'Rescheduled', color: '#ffbf00ff', backgroundColor: '#FBB32C1A' },
  "Cancelled": { label: 'Cancelled', color: '#E9004A', backgroundColor: '#E91E631A' },
  "Deleted": { label: 'Deleted', color: '#ff0000ff', backgroundColor: '#E9004A1A' }
};

const InterviewTable: React.FC<{ id?: string }> = ({ id }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [sortField, setSortField] = useState<keyof InterviewType | undefined>("createdDate");
  const [interviewData, setInterviewData] = useState<InterviewApiResponse | null>(null);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    const fetchInterviewData = async () => {
      try {
        setIsLoading(true);
        const response = await api.get<InterviewApiResponse>(
          APPLICANT_ENDPOINTS.interviews(id),
          {
            headers: { ...API_HEADERS.APPLICANT_HISTORY_INTERVIEWS },
          }
        );
        setInterviewData(response.data);
      } catch (error) {
        console.error('Error fetching interview data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInterviewData();
  }, [id]);

  // Helper function to determine interview status
  const getInterviewStatus = (interview: Interview): string => {
    if (interview.isDelete === 1) {
      if (interview.cancellationOption === 1) {
        return 'Cancelled';
      } else {
        return 'Deleted';
      }
    } else {
      if (interview.rescheduleCount > 0) {
        return `Rescheduled (${interview.rescheduleCount})`;
      } else {
        return 'Scheduled';
      }
    }
  };

  // Helper function to format date and time without seconds
  const formatDateTime = (dateString: string): string => {
    try {
      return dayjs(dateString).format('YYYY.MM.DD HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  // Helper function to format date only
  const formatDateOnly = (dateString: string): string => {
    try {
      return dayjs(dateString).format('YYYY.MM.DD');
    } catch (error) {
      return dateString;
    }
  };

  // Helper function to format confirmed date
  const formatConfirmedDate = (followUpStatus: number, followUpConfirmedAt: string | null): string => {
    if (followUpStatus === 1 && followUpConfirmedAt) {
      return formatDateTime(followUpConfirmedAt);
    }
    return 'N/A';
  };

  const columns: Column<InterviewType>[] = [
    { field: 'interviewType', headerName: 'Interview Type', width: "150px", sortable: true },
    { field: 'createdDate', headerName: 'Created Date', width: "120px", sortable: true, align: 'center' },
    { field: 'interviewDate', headerName: 'Interview Date', width: "150px", sortable: true, align: 'center' },
    { field: 'confirmationDate', headerName: 'Confirmation Date', width: "120px", sortable: true, align: 'center' },
    { field: 'confirmedDate', headerName: 'Confirmed Date', width: "120px", sortable: true, align: 'center' },
    {
      field: 'status', headerName: 'Status', width: "150px", sortable: true, align: 'center',
      renderCell: (row) => {
        const statusLabel = row.status.startsWith('Rescheduled') ? 'Rescheduled' : row.status;
        return (
          <StatusChip
            label={row.status}
            statusConfig={interviewStatusConfig}
            status={statusLabel}
          />
        );
      }
    },
    {
      field: 'interviewPanel', headerName: 'Interview Panel', width: "300px", sortable: false,
      renderCell: (row) => {
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {row.interviewPanel.map((panelMember) => (
              <Tooltip
                key={panelMember.id}
                title={panelMember.interviewerName || 'N/A'}
                arrow
              >
                <Chip
                  label={panelMember.interviewerEmail}
                  size="small"
                  sx={{
                    fontSize: '12px',
                    height: '24px',
                    backgroundColor: '#5656FF1A',
                    color: '#5656FF',
                    borderRadius: '10px',
                    '&:hover': {
                      backgroundColor: '#5656FF1A',
                    }
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        );
      }
    },
  ];

  const onSort = (field: keyof InterviewType) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <CustomTable<InterviewType>
      columns={columns}
      rows={interviewData?.interviews.map(item => ({
        interviewType: item.interviewType,
        createdDate: formatDateOnly(item.createdAt),
        interviewDate: `${formatDateOnly(item.date)} - ${item.startTime}`,
        confirmationDate: formatDateOnly(item.followUpDate),
        confirmedDate: formatConfirmedDate(item.followUpStatus, item.followUpConfirmedAt),
        status: getInterviewStatus(item),
        interviewPanel: item.panel
      })) ?? []}
      hasPagination={false}
      page={0}
      rowsPerPage={100}
      totalCount={interviewData?.interviews.length ?? 0}
      onPageChange={() => { }}
      onRowsPerPageChange={() => { }}
      onSort={onSort}
      sortField={sortField}
      sortDirection={sortDirection}
    />
  );
};

const HistoryTable: React.FC<{ id?: string }> = ({ id }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [sortField, setSortField] = useState<keyof HistoryType | undefined>("createdDate");
  const [historyData, setHistoryData] = useState<HistoryApiResponse | null>(null);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    const fetchHistoryData = async () => {
      try {
        setIsLoading(true);
        const response = await api.get<HistoryApiResponse>(
          APPLICANT_ENDPOINTS.history(id, sortField ?? 'createdDate', sortDirection),
          {
            headers: { ...API_HEADERS.APPLICANT_HISTORY_TIMELINE },
          }
        );
        setHistoryData(response.data);
      } catch (error) {
        console.error('Error fetching history data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchHistoryData();
  }, [id, sortField, sortDirection]);

  const columns: Column<HistoryType>[] = [
    {
      field: 'statusFrom', headerName: 'Transition', width: "200px", sortable: true,
      renderCell: (row) => {
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <StatusChip label={row.statusFrom} statusConfig={statusConfig} status={row.statusFrom} />
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 7H11.1667" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M7 2.9165L11.0833 6.99984L7 11.0832" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <StatusChip label={row.statusTo} statusConfig={statusConfig} status={row.statusTo} />
          </Box>
        )
      }
    },
    { field: 'createdDate', headerName: 'When', width: "200px", sortable: true },
    { field: 'createdBy', headerName: 'By', width: "200px", sortable: true },
    { field: 'timeInStatus', headerName: 'Time in Status', width: "200px", sortable: true },
  ];

  const onSort = (field: keyof HistoryType) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Helper function to format time in status
  const formatTimeInStatus = (timeInStatus: number): string => {
    if (timeInStatus === 0) return '0 hr';

    const hours = Math.floor(timeInStatus / 60);
    const remainingMinutes = timeInStatus % 60;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    if (days > 0) {
      return `${days} ${days === 1 ? 'day' : 'days'} ${remainingHours > 0 ? `, ${remainingHours} ${remainingHours === 1 ? 'hr' : 'hrs'}` : ''}`;
    } else if (hours > 0) {
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ${remainingMinutes > 0 ? `, ${remainingMinutes} ${remainingMinutes === 1 ? 'min' : 'mins'}` : ''}`;
    } else {
      return `${timeInStatus} ${timeInStatus === 1 ? 'min' : 'mins'}`;
    }
  };

  // Helper function to format date
  const formatDateTime = (dateString: string): string => {
    try {
      return dayjs(dateString, 'YYYY.MM.DD hh.mm').format('YYYY.MM.DD HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <CustomTable<HistoryType>
      columns={columns}
      rows={historyData?.history.map(item => ({
        statusFrom: item.statusFrom,
        statusTo: item.statusTo,
        createdDate: formatDateTime(item.createdDate),
        createdBy: item.createdBy,
        timeInStatus: formatTimeInStatus(item.timeInStatus)
      })) ?? []}
      hasPagination={false}
      page={0}
      rowsPerPage={100}
      totalCount={historyData?.history.length ?? 0}
      onPageChange={() => { }}
      onRowsPerPageChange={() => { }}
      onSort={onSort}
      sortField={sortField}
      sortDirection={sortDirection}
    />
  );
};

const History: React.FC<HistoryProps> = ({ setSummary }) => {
  const { id } = useParams();
  const [refresh, setRefresh] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditFirst, setIsEditFirst] = useState(false);
  const [isEditSecond, setIsEditSecond] = useState(false);
  const { pageId, setPageId, hasPermission } = usePermissions();
  const { notification, hideNotification, showNotification } = useNotification();
  const [values, setValues] = useState<ApplicantProfileData['basicInfo'] | null>(null);
  const [initialValues, setInitialValues] = useState<ApplicantProfileData['basicInfo'] | null>(null);
  const [sourceReferralError, setSourceReferralError] = useState('');

  useEffect(() => {
    if (!id || pageId !== PAGE_IDS.APPLICANT_HISTORY) {
      setPageId(PAGE_IDS.APPLICANT_HISTORY);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const fetchApplicantData = async () => {
      const response = await api.get<{ data: ApplicantProfileData['basicInfo'] }>(APPLICANT_ENDPOINTS.basic(id),
        {
          headers: { ...API_HEADERS.APPLICANT_HISTORY_BASIC_GET },
        }
      );
      setSummary(response.data.data);
      setValues(response.data.data);
      setInitialValues(response.data.data);
      setIsLoading(false);
    };

    fetchApplicantData();
  }, [id, refresh, setSummary, pageId, setPageId]);

  if (isLoading || !values) {
    return <Loading />;
  }

  const infoFields = [
    {
      label: 'Current designation',
      value: values?.currentDesignation,
      width: 'calc(25% - 12px)'
    },
    {
      label: 'Source',
      value: values?.source,
      width: 'calc(25% - 12px)'
    },
    {
      label: 'Source Referral',
      value: values?.sourceReferral,
      width: 'calc(25% - 12px)'
    },
  ];

  const historyFields = [
    {
      label: 'Applied Date',
      value: values?.createdAt ? formatDate(values.createdAt) : '',
      width: 'calc(25% - 12px)'
    },
    {
      label: 'Application Status',
      value: <StatusChip
        label={values?.currentStatus}
        statusConfig={statusConfig}
        status={values?.currentStatus}
      />,
      width: 'calc(25% - 12px)'
    },
  ];

  const handleLoadDesignations = async (query: string) => {
    const response = await api.get<{ data: { id: number; designation: string }[] }>(
      SYSTEM_ENDPOINTS.designations,
      {
        headers: { ...API_HEADERS.COUNTRIES_LOOKUP },
      }
    );

    return response.data.data.map((item) => ({
      value: item.id.toString(),
      label: item.designation,
    }));
  };

  const handleLoadStatuses = async (query: string) => {
    const response = await api.get<{ data: { id: number; name: string }[] }>(
      SETTINGS_ENDPOINTS.applicantStatuses,
      {
        headers: { ...API_HEADERS.APPLICANT_HISTORY_STATUSES_LOOKUP },
      }
    );

    return response.data.data.map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  const handleSaveChanges = async () => {
    const applicantId = id;
    if (applicantId === undefined) {
      return;
    }

    const sourceReferralValue = (values?.sourceReferral ?? '').trim();
    if (sourceReferralValue && !isValidEmail(sourceReferralValue)) {
      setSourceReferralError('Source Referral must be a valid email address');
      return;
    }
    setSourceReferralError('');

    try {
      const payload = {
        issueId: values?.issueId,
        designation: values?.currentDesignation,
        designationId: values?.currentDesignationId,
        source: values?.source?.charAt(0).toUpperCase() + values?.source?.slice(1),
        sourceReferral: values?.sourceReferral,
        createdAt: values?.createdAt,
        previousStatus: initialValues?.currentStatus,
        previousStatusId: initialValues?.currentStatusId,
        currentStatus: values?.currentStatus,
        currentStatusId: values?.currentStatusId,
      }

      setIsSaving(true);
      await api.put(APPLICANT_ENDPOINTS.updateHistory(applicantId), payload,
        {
          headers: { ...API_HEADERS.APPLICANT_HISTORY_UPDATE },
        }
      );

      showNotification('Changes saved successfully', 'success');
      setRefresh(refresh + 1);
    } catch (error) {
      showNotification('Error saving changes', 'error');
      console.error('Error saving changes:', error);
    } finally {
      setIsEditFirst(false);
      setIsEditSecond(false);
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Box className="view-section">
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: '-12px', right: '-12px' }}>
            {!isEditFirst && hasPermission(PERMISSION_IDS.APPLICANT_HISTORY_EDIT) && (
              <IconButton className="view-edit-icon" onClick={() => setIsEditFirst(true)}>
                <EditIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {infoFields.map((field) => (
              <Box key={field.label} sx={{ width: field.width }}>
                <Typography className="view-label-title">{field.label}</Typography>
                {
                  isEditFirst ? (
                    <>
                      {field.label === 'Current designation' && (
                        <AsyncAutocomplete
                          loadOptions={handleLoadDesignations}
                          value={values?.currentDesignation ? { value: values.currentDesignationId?.toString() || values.currentDesignation || '', label: values.currentDesignation || '' } : null}
                          onChange={(value) => setValues({ ...values, currentDesignationId: value?.value ? parseInt(value.value) : undefined, currentDesignation: value?.label || '' })}
                        />
                      )}
                      {field.label === 'Source' && (
                        <SelectField
                          options={[
                            { value: 'Direct', label: 'Direct' },
                            { value: 'Linkedin', label: 'Linkedin' },
                            { value: 'Agency', label: 'Agency' },
                            { value: 'Website', label: 'Website' },
                            { value: 'Internal Referral', label: 'Internal Referral' },
                            { value: 'External Referral', label: 'External Referral' },
                          ]}
                          value={values?.source}
                          onChange={(e) => setValues({ ...values, source: e.target.value as string })}
                        />
                      )}
                      {field.label === 'Source Referral' && (
                        <TextField
                          value={values?.sourceReferral}
                          error={!!sourceReferralError}
                          helperText={sourceReferralError}
                          onChange={(e) => {
                            setSourceReferralError('');
                            setValues({ ...values, sourceReferral: e.target.value as string });
                          }}
                          onBlur={() => {
                            const referral = (values?.sourceReferral ?? '').trim();
                            if (referral && !isValidEmail(referral)) {
                              setSourceReferralError('Source Referral must be a valid email address');
                            }
                          }}
                        />
                      )}
                    </>
                  ) : <Typography className="view-value-title">{field.value}</Typography>}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box className="view-section">
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: '-12px', right: '-12px' }}>
            {!isEditSecond && hasPermission(PERMISSION_IDS.APPLICANT_HISTORY_EDIT) && (
              <IconButton className="view-edit-icon" onClick={() => setIsEditSecond(true)}>
                <EditIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {historyFields.map((field) => (
              <Box key={field.label} sx={{ width: field.width }}>
                <Typography className="view-label-title">{field.label}</Typography>
                {
                  isEditSecond ? (
                    <>
                      {field.label === 'Applied Date' && (
                        <DatePickerField
                          value={dayjs(values?.createdAt)}
                          onChange={(date: string | null) => setValues({ ...values, createdAt: date ? date : '' })}
                        />
                      )}
                      {field.label === 'Application Status' && (
                        <AsyncAutocomplete
                          loadOptions={handleLoadStatuses}
                          value={values?.currentStatus ? { value: values.currentStatusId?.toString() || values.currentStatus || '', label: values.currentStatus || '' } : null}
                          onChange={(value) => setValues({ ...values, currentStatusId: value?.value ? parseInt(value.value) : undefined, currentStatus: value?.label || '' })}
                        />
                      )}
                    </>
                  ) : <Typography className="view-value-title">{field.value}</Typography>}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box className="view-section">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <div className="view-section-title">Applicant Interview</div>
        </Box>
        <InterviewTable id={id} />
      </Box>
      <Box className="view-section">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <div className="view-section-title">Application History</div>
        </Box>
        <HistoryTable id={id} />
      </Box>

      {/* Action Buttons */}
      {(isEditFirst || isEditSecond) && hasPermission(PERMISSION_IDS.APPLICANT_HISTORY_EDIT) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, width: '1246px', mt: 1 }}>
          <Button
            variant="outlined"
            disabled={isSaving}
            className="edit-section-cancel-button"
            onClick={() => {
              setIsEditFirst(false);
              setIsEditSecond(false);
              setValues(initialValues);
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            disabled={isSaving}
            isLoading={isSaving}
            onClick={handleSaveChanges}
            className="edit-section-save-button"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}


      {/* Notification Component */}
      <Notification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        severity={notification.severity}
        title={notification.title}
      />
    </Box>
  );
};

export default History; 